// src/components/eligibilityConfig10.js

// Credits we are analyzing
export const CREDIT_TYPES = [
  { id: "eitc", label: "EITC", long: "Earned Income Tax Credit" },
  { id: "savers", label: "Saver's", long: "Saver's Credit" },
  { id: "education", label: "Education", long: "Education Credits" },
  { id: "ctc", label: "CTC", long: "Child Tax Credit" },
  { id: "odc", label: "ODC", long: "Other Dependent Credit" },
  { id: "actc", label: "ACTC", long: "Additional Child Tax Credit" },
  { id: "cdc", label: "CDC", long: "Child & Dependent Care Credit" },
];

// Internal statuses per question:
// "yes"   → likely OK on this question
// "no"    → likely blocks the credit
// "maybe" → depends on details not asked here

function combineStatuses(...statuses) {
  if (statuses.includes("no")) return "no";
  if (statuses.includes("maybe")) return "maybe";
  return "yes";
}

/**
 * answers:
 * {
 *   ssn: "yes" | "no" | null,
 *   earnedIncome: "yes" | "no" | null,
 *   canBeClaimed: "yes" | "no" | null,
 *   ageBand: "under18" | "18to24" | "25to64" | "65plus" | null,
 *   hasQualifyingChild: "yes" | "no" | null,
 *   fullTimeStudent: "yes" | "no" | null,
 *   paidTuition: "yes" | "no" | null,
 *   retirementContribution: "yes" | "no" | null,
 *   investmentIncomeHigh: "yes" | "no" | null,
 *   incomeLevel: "low" | "medium" | "high" | null,
 * }
 */

export function evaluateEligibility(answers, selectedCreditIds) {
  const {
    ssn,
    earnedIncome,
    canBeClaimed,
    ageBand,
    hasQualifyingChild,
    fullTimeStudent,
    paidTuition,
    retirementContribution,
    investmentIncomeHigh,
    incomeLevel,
  } = answers;

  const results = {};

  selectedCreditIds.forEach((creditId) => {
    // Q1 – Work-authorized SSN?
    // Educational simplification: if no SSN, all 7 credits are blocked on *this* return.
    let q1;
    if (ssn === "no") q1 = "no";
    else if (ssn === "yes") q1 = "yes";
    else q1 = "maybe";

    // Q2 – Earned income?
    let q2;
    if (earnedIncome == null) {
      q2 = "maybe";
    } else if (earnedIncome === "yes") {
      q2 = "yes";
    } else {
      // earnedIncome === "no"
      if (["eitc", "savers", "actc", "cdc"].includes(creditId)) {
        // these really lean on earned income
        q2 = "no";
      } else if (["education", "ctc", "odc"].includes(creditId)) {
        // can still exist with only other taxable income if there is tax due
        q2 = "maybe";
      } else {
        q2 = "maybe";
      }
    }

    // Q3 – Can someone else claim you?
    // From our chart: if you *can* be claimed by someone else, these credits
    // don't appear on *your* own return.
let q3;

if (canBeClaimed == null) {
  // We don't know yet if someone can claim you
  q3 = "maybe";
} else if (canBeClaimed === "yes") {
  // You CAN be claimed as a dependent

  if (["eitc", "ctc", "actc", "savers", "odc", "cdc"].includes(creditId)) {
    // For these, being a dependent basically blocks you
    q3 = "no";
  } else {
    // Saver's, Education, ODC, CDC etc.
    // Being a dependent does NOT automatically kill the credit;
    // it just means we need more info.
    q3 = "maybe";
  }

} else {
  // canBeClaimed === "no" → no one else can claim you
  q3 = "yes";
}


    // Q4 – Age band (taxpayer age)
    let q4;
    if (!ageBand) {
      q4 = "maybe";
    } else {
      if (creditId === "eitc") {
        // Educational simplification based on your chart:
        // Under 18 → ✅ if not a dependent (we already handled dependency in Q3)
        // 18–24, 25–64, 65+ → ✅ (we're not modeling the detailed childless EITC rules here)
        q4 = "yes";
      } else if (creditId === "savers") {
        // Must be at least 18, and not a student (student part handled in Q6)
        if (ageBand === "under18") q4 = "no";
        else q4 = "yes";
      } else {
        // Education, CTC, ODC, ACTC, CDC:
        // Taxpayer age itself is not usually the blocking factor
        q4 = "yes";
      }
    }

    // Q5 – Qualifying children (for this filer)
    let q5;
    if (!hasQualifyingChild) {
      q5 = "maybe";
    } else if (hasQualifyingChild === "yes") {
      if (["ctc", "actc", "cdc"].includes(creditId)) {
        // These are directly child/qualifying-person based
        q5 = "yes";
      } else if (creditId === "eitc") {
        // EITC can exist with or without children, but child patterns differ
        q5 = "maybe";
      } else {
        // Saver's, Education, ODC
        q5 = "maybe";
      }
    } else {
      // hasQualifyingChild === "no"
      if (["ctc", "actc"].includes(creditId)) {
        q5 = "no"; // no qualifying child, no CTC/ACTC for this filer
      } else if (creditId === "cdc") {
        // could still have a qualifying dependent adult needing care, so "maybe"
        q5 = "maybe";
      } else {
        // EITC childless, Saver's, ODC, Education
        q5 = "maybe";
      }
    }

    // Q6 – Full-time student?
    let q6;
    if (!fullTimeStudent) {
      q6 = "maybe";
    } else if (fullTimeStudent === "yes") {
      if (creditId === "savers") {
        // Saver's credit blocked for full-time students
        q6 = "no";
      } else if (creditId === "education") {
        // Education credit obviously still okay
        q6 = "yes";
      } else if (creditId === "eitc") {
        // detailed rules are tricky; for teaching, we keep this a "maybe"
        q6 = "maybe";
      } else {
        // CTC, ODC, ACTC, CDC mostly unaffected by taxpayer being a FT student
        q6 = "yes";
      }
    } else {
      // fullTimeStudent === "no"
      if (creditId === "savers") q6 = "yes";
      else q6 = "yes";
    }

    // Q7 – Paid tuition?
    let q7;
    if (!paidTuition) {
      q7 = "maybe";
    } else if (paidTuition === "yes") {
      if (creditId === "education") q7 = "yes";
      else q7 = "yes"; // other credits unaffected
    } else {
      // paidTuition === "no"
      if (creditId === "education") q7 = "no";
      else q7 = "yes";
    }

    // Q8 – Retirement contribution?
    let q8;
    if (!retirementContribution) {
      q8 = "maybe";
    } else if (retirementContribution === "yes") {
      if (creditId === "savers") q8 = "yes";
      else q8 = "yes";
    } else {
      // "no" retirement contribution
      if (creditId === "savers") q8 = "no";
      else q8 = "yes";
    }

    // Q9 – Investment income high?
    let q9;
    if (!investmentIncomeHigh) {
      q9 = "maybe";
    } else if (investmentIncomeHigh === "yes") {
      if (creditId === "eitc") q9 = "no"; // high investment income kills EITC
      else q9 = "yes";
    } else {
      // "no" → investment income below threshold
      q9 = "yes";
    }

    // Q10 – Earned income level (low/medium/high)
    let q10;
    if (!incomeLevel) {
      q10 = "maybe";
    } else if (incomeLevel === "low") {
      q10 = "yes"; // low income usually within ranges for these credits
    } else if (incomeLevel === "medium") {
      // Many still in play, but some phasing out
      if (["eitc", "savers", "education"].includes(creditId)) {
        q10 = "maybe";
      } else {
        q10 = "yes"; // CTC/ODC/ACTC/CDC often still available in much of medium band
      }
    } else {
      // incomeLevel === "high"
      if (["eitc", "savers", "actc", "education"].includes(creditId)) {
        // commonly phased out at high incomes
        q10 = "no";
      } else {
        // CTC, ODC, CDC have higher phaseout thresholds;
        // keep "maybe" so you can explain phaseouts on a follow-up screen.
        q10 = "maybe";
      }
    }

    const status = combineStatuses(q1, q2, q3, q4, q5, q6, q7, q8, q9, q10);

    results[creditId] = {
      status,
      details: { q1, q2, q3, q4, q5, q6, q7, q8, q9, q10 },
    };
  });

  return results;
}
